/**
 * @author Ka Wing Chin <kawing.chin@takeaway.com>
 * @class Takeaway.Restaurant
 * The restaurant.
 */

import ENUM from '../lib/enums'
import Utils from '../lib/utils'

export default class Restaurant {
  // TODO normally Restaurant should have Basket object, but the code in this project doesn't make any sense.
  // TODO or even better use the Repository pattern to decouple the models.
  constructor () {
    this.openingTimes = []
    this.pickupOpeningTimes = []
    this.exceptionTimes = []
    this.pickupExceptionTimes = []
    this.deliveryAreas = []
    this.categories = []
    this.autoAddProducts = []
    this.discounts = []
    this.paymentMethods = []
  }

  /**
   * @enum Restaurant OpeningStatus
   * @property {Number} OpeningStatus.Closed Restaurant is closed.
   * @property {Number} OpeningStatus.Open Restaurant is open.
   * @property {Number} OpeningStatus.PreOrder Restaurant can be preordered.
   */
  static OpeningStatus = Object.freeze({
    Closed: 0,
    Open: 1,
    PreOrder: 2
  })

  id
  name
  postcode
  url
  cartUrl
  orderUrl
  domain
  /**
   * @property {Restaurant.OpeningStatus} onlineStatus The opening status independed from the time.
   */
  onlineStatus
  /**
   * @property {Restaurant.OpeningStatus} openingStatus The opening status calculated with time.
   */
  openingStatus
  /**
   * @property {Array<TimeWindow>} openingTimes
   */
  openingTimes
  /**
   * @property {Array<TimeWindow>} pickupOpeningTimes
   */
  pickupOpeningTimes
  /**
   * @property {Array<TimeWindow>} exceptionTimes
   */
  exceptionTimes
  /**
   * @property {Array<TimeWindow>} pickupExceptionTimes
   */
  pickupExceptionTimes
  /**
   * @property {Array<DeliveryArea>} deliveryAreas
   */
  deliveryAreas
  /**
   * @property {ENUM.DeliveryMethod.Type} deliveryType the restaurant supports
   */
  deliveryType
  /**
   * @property {string} Current delivery area id
   */
  deliveryAreaId

  categories
  noDeliveryCostsFrom
  autoAddProducts
  discounts
  paymentMethods
  /**
   * WARNING OBJECT LITERAL FROM PHP
   */
  translations = {}
  /**
   * WARNING OBJECT LITERAL FROM PHP
   */
  currency

  /**
   * Get the opening status of the restaurant according the server time. The result will be set on openingStatus.
   * @param {ServerDateTime} serverDateTime time and weekday from the server.
   * @param {ENUM.DeliveryMethod.Type} deliveryType the delivery type the status needs to check on.
   * @return {Restaurant.OpeningStatus} the status of the opening.
   */
  getOpeningStatus (serverDateTime, deliveryType) {
    var openingTimes = []
    // Check if there is an exception time for today.
    if (this.exceptionTimes.length > 0 || this.pickupExceptionTimes.length > 0) {
      var times = (deliveryType === ENUM.DeliveryMethod.Type.Delivery) ? this.exceptionTimes : this.pickupExceptionTimes
      var date = Utils.getDate(serverDateTime.time)
      openingTimes = times.filter(function (exceptionTime) {
        return exceptionTime.date.isSame(date, 'date')
      })
    }

    // Do the regular times if there are no exception times.
    if (openingTimes.length === 0) {
      var times = (deliveryType === ENUM.DeliveryMethod.Type.Delivery) ? this.openingTimes : this.pickupOpeningTimes
      openingTimes = times.filter(function (openingTime) {
        return openingTime.weekDay === serverDateTime.weekDay
      })
    }

    for (var x in openingTimes) {
      var openingTime = openingTimes[x]
      var startTime = Utils.timeToToday(openingTime.startTime, serverDateTime.time)
      var endTime = Utils.timeToToday(openingTime.endTime, serverDateTime.time)

      // if opening time is set to 00:00 then the restaurant is closed.
      if (Utils.getSeconds(startTime) === 0 && Utils.getSeconds(endTime) === 0) {
        this.openingStatus = Restaurant.OpeningStatus.Closed
        return this.openingStatus
      }

      var timeStatus = Utils.checkTimeBetween(openingTime, serverDateTime.time, serverDateTime.time)
      if (timeStatus === Utils.TimeStatus.Before) {
        this.openingStatus = Restaurant.OpeningStatus.PreOrder
        return this.openingStatus
      }
      else if (timeStatus === Utils.TimeStatus.Between) {
        this.openingStatus = Restaurant.OpeningStatus.Open
        return this.openingStatus
      }
      else {
        this.openingStatus = Restaurant.OpeningStatus.Closed
      }
    }
    return this.openingStatus
  }

  /**
   * Is the restaurant closed or not.
   * getOpeningStatus() should have been called before this method.
   * @return {boolean} Whether the restaurant is closed or not.
   */
  isClosed () {
    if (this.onlineStatus === Restaurant.OpeningStatus.Closed || this.openingStatus === Restaurant.OpeningStatus.Closed)
      return true
    return false
  }

  /**
   * Get the category by id.
   * @param {string} id of the category.
   * @return {Takeaway.Category} the category object.
   */
  getCategoryById (id) {
    return this.categories.filter(function (category) {
      return id === category.id
    })[0]
  }

  /**
   * Validate all category times whether they are open or closed.
   * Not the best place to put it here. Something like MenuRepository would be handy.
   * @param {ServerDateTime} serverDateTime
   */
  validateCategoriesOrderTime (serverDateTime) {
    for (var x in this.categories) {
      var category = this.categories[x]
      category.validateOrderTimeByServerTime(serverDateTime)
    }
  }

  /**
   * Get the delivery area by id.
   * @param {string} id of the delivery area.
   * @return {Takeaway.DeliveryArea} the delivery area object.
   */
  getDeliveryAreaById (id) {
    return this.deliveryAreas.filter(function (deliveryArea) {
      return id === deliveryArea.id
    })[0]
  }
}
